<template>
    <div
        v-if="!$wait.is('loading-page')"
        class="m-pwa"
    >
        <div class="m-pwa__hero">
            <y-container>
                <y-hero :content="hero" />
            </y-container>
        </div>
        <div class="m-pwa__features-slider">
            <y-container>
                <y-features-slider :slider-data="discoverFeatures" />
            </y-container>
        </div>
        <div class="m-pwa__video">
            <y-container>
                <y-pwa-video
                    :streaming-data="videoData"
                    dont-show-content
                />
            </y-container>
        </div>
        <div class="m-pwa__types">
            <y-container>
                <y-pwa-types
                    :pwa-types="exploreTypes"
                />
            </y-container>
        </div>
        <div class="m-pwa__benefits">
            <y-container>
                <y-hero
                    :content="benefits"
                    hide-image-on-mobile
                    title-center-on-mobile
                    hide-background-image
                    tag="h3"
                />
            </y-container>
        </div>
        <div class="m-pwa__power-of-pwa">
            <y-container>
                <y-power-of-pwa :content="powerOfPWA" />
            </y-container>
        </div>
        <div class="m-pwa__transforming">
            <y-container>
                <y-transforming :content="transforming" />
            </y-container>
        </div>
        <div class="m-pwa__case-study">
            <y-container>
                <y-case-study
                    :case-studies-data="caseStudies"
                />
            </y-container>
        </div>
        <div class="m-pwa__call-to-action">
            <y-container>
                <y-call-to-action
                    :content="callToAction"
                />
            </y-container>
        </div>
    </div>
</template>

<script>

    // Mixins
    import PageMixin from '@groot-nodes/mixins/Page.js';

    // Helpers
    import { removeSymbol, formatStats } from '@/helpers/helpers';

    // Components
    import YHero from '@modules/enterprise/components/Hero/Index.vue';
    import YCaseStudy from '@components/CaseStudy/CaseStudy.vue';
    import YPwaVideo from '@modules/pwa/components/PwaVideo/Index.vue';
    import YPwaTypes from '@modules/pwa/components/PwaTypes/index.vue';
    import YFeaturesSlider from '@modules/pwa/components/FeaturesSlider/Index.vue';
    import YCallToAction from '@/modules/pwa/components/CallToAction/Index';
    import YPowerOfPwa from '@/modules/pwa/components/PowerOfPwa/Index';
    import YTransforming from '@/modules/pwa/components/Transforming/Index';

    export default {
        name: 'PWAPage',

        components: {
            YHero, YCallToAction, YFeaturesSlider, YPwaVideo, YPwaTypes, YCaseStudy, YPowerOfPwa, YTransforming,
        },

        mixins: [PageMixin],

        /**
         * @inheritdoc
         */
        data() {
            return {
                hero            : '',
                discoverFeatures: {},
                videoData       : {},
                exploreTypes    : {},
                benefits        : {},
                powerOfPWA      : {},
                transforming    : {},
                caseStudies     : {},
                callToAction    : {},
            };
        },

        /**
         * @inheritDoc
         */
        metaInfo() {
            return {
                title: this.hero.seo_post_title,

                meta: [
                    {
                        vmid   : 'description',
                        name   : 'description',
                        content: this.hero.seo_meta_description,
                    },
                ],
            };
        },

        /**
         *
         * @inheritDoc
         */
        methods: {
            removeSymbol,
            formatStats,

            /**
             * Fetches PWA page data
             *
             * @returns {Promise} Promise object asigns the components data when resolved
             */
            fetch() {
                // Fetches the hero and features sections data
                const heroAndFeatures = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'pwa-hero',
                    including: ['title', 'synopsis', 'image', 'text', 'custom', 'slug', 'albums', 'title2', 'seo_canonical', 'seo_meta_description', 'seo_post_title'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                // Fetches the video, types and benefits sections data
                const videoTypesBenefits = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'pwa-how-it-works',
                    including: ['title', 'synopsis', 'image', 'text', 'custom', 'slug', 'albums', 'title2', 'header_image'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                // Fetches the power of PWA, transforming, case studies and CTA sections data
                const powerOfPwaTransformingCTA = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'pwa-stat-data',
                    including: ['title', 'synopsis', 'image', 'text', 'custom', 'slug', 'albums', 'title2'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                const caseStudies = this.$services.Content.postList({
                    type     : 'case-studies',
                    tagging  : 'pwa',
                    including: ['title', 'synopsis', 'image', 'custom', 'url_encode', 'albums', 'slug'],
                    locale   : this.$i18n.locale,
                    limit    : 3,
                }).catch((error) => {
                    this.handleError(error);
                });

                return Promise.all(
                    [heroAndFeatures, videoTypesBenefits, powerOfPwaTransformingCTA, caseStudies],
                ).then((responses) => {
                    if (this.handleReq(responses, 0)) {
                        this.hero = {
                            title               : responses[0].data.results.title,
                            synopsis            : responses[0].data.results.title2,
                            image               : responses[0].data.results.image,
                            seo_canonical       : responses[0].data.results.seo_canonical,
                            seo_meta_description: responses[0].data.results.seo_meta_description,
                            seo_post_title      : responses[0].data.results.seo_post_title,
                            custom              : {
                                'button-link' : responses[0].data.results.custom['button-link'],
                                'button-title': responses[0].data.results.custom['button-title'],
                            },
                        };

                        this.discoverFeatures = {
                            title : responses[0].data.results.synopsis,
                            albums: responses[0].data.results.albums,
                            custom: {
                                'button-title': responses[0].data.results.custom.field1,
                                'button-link' : responses[0].data.results.custom.field2,
                            },
                        };
                    }
                    if (this.handleReq(responses, 1)) {
                        this.videoData = {
                            title   : responses[1].data.results.title,
                            synopsis: responses[1].data.results.synopsis,
                            albums  : responses[1].data.results.albums.filter((item) => item.group === 'video'),
                            image   : responses[1].data.results.image,
                            custom  : {
                                embed_link: responses[1].data.results?.custom?.field5,
                            },
                        };

                        this.exploreTypes = {
                            title   : responses[1].data.results.title2,
                            synopsis: responses[1].data.results.synopsis,
                            albums  : responses[1].data.results.albums.filter((item) => item.group === 'album'),
                        };

                        this.benefits = {
                            title   : responses[1].data.results.custom.field1,
                            synopsis: responses[1].data.results.custom.field2,
                            image   : responses[1].data.results.header_image,
                            custom  : {
                                'button-title': responses[1].data.results.custom.field3,
                                'button-link' : responses[1].data.results.custom.field4,
                            },
                        };
                    }
                    if (this.handleReq(responses, 2)) {
                        this.powerOfPWA = {
                            title: responses[2].data.results.title,
                            stats: this.formatStats(responses[2].data.results.synopsis),
                        };
                        this.transforming = {
                            title   : responses[2].data.results.custom.field1,
                            synopsis: responses[2].data.results.custom.field2,
                            albums  : responses[2].data.results.albums,
                        };
                        this.caseStudies = {
                            ...this.caseStudies,
                            title : responses[2].data.results.custom.field3,
                            custom: {
                                'button-title': responses[2].data.results.custom['button-title'],
                                'button-link' : responses[2].data.results.custom['button-link'],
                            },
                        };
                        this.callToAction = {
                            title : responses[2].data.results.custom.field4,
                            custom: {
                                'button-title': responses[2].data.results.custom.field5,
                                'button-link' : responses[2].data.results.custom.field6,
                            },
                        };
                    }
                    if (this.handleReq(responses, 3)) {
                        this.caseStudies = {
                            ...this.caseStudies,
                            albums: responses[3].data.results.map((item) => ({
                                title      : item.title,
                                url_encode : item.url_encode,
                                description: item.synopsis,
                                slug       : item.slug,
                                ...item.image,
                            })),
                        };
                    }
                }).catch((error) => {
                    this.handleError(error);
                });
            },
        },
    };
</script>

<style lang="scss" src="../scss/pwa.scss"></style>
